"use client"

import {
  certifications,
  coreStack,
  education,
  skillGroups,
} from "@/lib/content"
import { useLocale } from "@/lib/locale-context"
import { Reveal } from "@/components/reveal"
import { SectionHead } from "@/components/section-head"

/** The full record: skill matrix, education, certifications and stack. */
export function SectionSkills() {
  const { t } = useLocale()
  const groupLabel = {
    core: t.alt.skillCore,
    platform: t.alt.skillPlatform,
    also: t.alt.skillAlso,
  }

  return (
    <section id="skills" className="shell scroll-mt-16 py-12 md:py-20" aria-labelledby="skills-heading">
      <SectionHead id="skills" index="05" title={t.alt.skills} />

      <dl className="mt-8 md:mt-12">
        {skillGroups.map((group) => (
          <Reveal key={group.key} className="rule grid12 py-5">
            <dt className="reveal t-small col-span-12 font-medium uppercase tracking-[0.06em] text-foreground md:col-span-2">
              {groupLabel[group.key]}
            </dt>
            <dd
              className="reveal t-small col-span-12 mt-2 text-muted-foreground md:col-span-9 md:col-start-4 md:mt-0"
              style={{ ["--d" as string]: "80ms" }}
            >
              {group.items}
            </dd>
          </Reveal>
        ))}
      </dl>

      <Reveal className="grid12 mt-10">
        <span className="reveal-rule col-span-12 mb-4 block h-[2px] bg-foreground" aria-hidden="true" />
        <p className="reveal t-small col-span-12 font-medium uppercase tracking-[0.06em] text-foreground md:col-span-2">
          {t.alt.education}
        </p>
        <ul className="col-span-12 md:col-span-9 md:col-start-4">
          {education.map((item, i) => (
            <li
              key={item.degree}
              className="reveal flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b border-border py-3 last:border-b-0"
              style={{ ["--d" as string]: `${i * 70}ms` }}
            >
              <div>
                <p className="t-small text-foreground">{item.degree}</p>
                <p className="t-small text-muted-foreground">{item.school}</p>
              </div>
              <span className="t-small tabular-nums text-muted-foreground">{item.years}</span>
            </li>
          ))}
        </ul>
      </Reveal>

      <Reveal className="grid12 mt-6">
        <span className="reveal-rule col-span-12 mb-4 block h-px bg-border" aria-hidden="true" />
        <p className="reveal t-small col-span-12 font-medium uppercase tracking-[0.06em] text-foreground md:col-span-2">
          {t.alt.certifications}
        </p>
        <p className="reveal t-small col-span-12 mt-2 text-muted-foreground md:col-span-9 md:col-start-4 md:mt-0">
          {certifications}
        </p>
      </Reveal>

      <Reveal className="grid12 mt-6">
        <span className="reveal-rule col-span-12 mb-4 block h-px bg-border" aria-hidden="true" />
        <p className="reveal t-small col-span-12 font-medium uppercase tracking-[0.06em] text-foreground md:col-span-2">
          {t.hero.stackLabel}
        </p>
        <ul className="col-span-12 mt-2 flex flex-wrap gap-x-5 gap-y-1 md:col-span-9 md:col-start-4 md:mt-0">
          {coreStack.map((item, i) => (
            <li
              key={item}
              className="reveal t-small text-foreground"
              style={{ ["--d" as string]: `${i * 40}ms` }}
            >
              {item}
            </li>
          ))}
        </ul>
      </Reveal>
    </section>
  )
}
